import { useId, useState, type Dispatch, type SetStateAction } from 'react';
import { Check, CaretDown, CircleNotch, X } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';
import { rawFileUrl } from '~/features/chat/image-source';
import { pickGateState, type DesignGateState } from './design-direction-gate';
import { DesignSuggestionImage } from './design-suggestion-image';
import { DesignSuggestionViewer } from './design-suggestion-viewer';
import { useDesignReferenceUploads } from './use-design-reference-uploads';
import { useDesignSuggestionPicks } from './use-design-suggestion-picks';
import {
  DESIGN_DIRECTIVES,
  DESIGN_DIRECTIVE_LABELS,
  DESIGN_REFERENCE_IMAGE_ACCEPT,
  MAX_DESIGN_INSPIRATION_CHARS,
  MAX_DESIGN_PICKS,
  type DesignDirective,
  type DesignDirectionDraft,
  type DesignSuggestion,
} from './design-direction';

/**
 * FRONT-2382 — the "Design direction" UI shared by the New-project form
 * (`DesignDirectionDisclosure`, below) and the Project-context dialog's own
 * editor (`~/features/coding/project-context-dialog.tsx`), which reuses the
 * two small presentational pieces here (`DesignDirectiveChips`,
 * `DesignReferenceThumb`) rather than a second copy of the chip/thumbnail
 * markup. Its own file for the same reason `project-context-dialog.tsx` got
 * one: `build-window.tsx` is already long, and this is a real
 * self-contained workflow (upload, suggest, pick), not a one-off field.
 *
 * `DesignDirectionDisclosure` itself is kept to composition + state — each
 * field is its own small function below it (`InspirationField`,
 * `ReferenceImagesField`, `SuggestionsField`) rather than one long function,
 * matching this repo's own function-length convention.
 */

/** Shared chip row — "How should EDDY use them?" on the form, the same four
 *  chips again in the Project-context dialog's editor. Hand-rolled buttons
 *  (no design-system control exists for a multi-select chip group),
 *  `aria-pressed` marks the toggle state per CLAUDE.md's chip convention. */
export function DesignDirectiveChips({
  selected,
  onToggle,
  disabled,
}: {
  selected: DesignDirective[];
  onToggle: (directive: DesignDirective) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {DESIGN_DIRECTIVES.map((directive) => {
        const active = selected.includes(directive);
        return (
          <button
            key={directive}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onToggle(directive)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-[length:var(--text-xs)] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              active
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-foreground/30 hover:bg-muted/50 hover:text-foreground',
            )}
          >
            {DESIGN_DIRECTIVE_LABELS[directive]}
          </button>
        );
      })}
    </div>
  );
}

/** Shared square thumbnail — a staged reference (upload or web pick) with an
 *  optional remove button, or a spinner placeholder while an upload is still
 *  in flight. Used by the form's upload row AND the Project-context dialog's
 *  reference list.
 *
 *  `onView` (feedback round 2) opens the shared zoomed viewer — only the
 *  Project-context dialog's reference row passes it; the New-project form's
 *  own upload thumbnails (`ReferenceImagesField`) are unaffected and stay
 *  plain. `onRemove`'s corner button is a SIBLING of the `onView` button
 *  (never nested — a button can't contain a button), so a click on it never
 *  reaches the view button underneath. */
export function DesignReferenceThumb({
  src,
  alt,
  loading,
  onRemove,
  removeLabel,
  onView,
  viewLabel,
  testId,
}: {
  src?: string;
  alt: string;
  loading?: boolean;
  onRemove?: () => void;
  removeLabel?: string;
  onView?: () => void;
  viewLabel?: string;
  testId?: string;
}) {
  return (
    <div
      className="relative size-16 shrink-0 overflow-hidden rounded-md border border-border bg-muted"
      data-testid={testId}
    >
      {loading ? (
        <div className="flex size-full items-center justify-center">
          <CircleNotch size={16} className="animate-spin text-muted-foreground" />
        </div>
      ) : src ? (
        onView ? (
          <button
            type="button"
            onClick={onView}
            aria-label={viewLabel ?? `View ${alt} large`}
            className="block size-full cursor-pointer"
          >
            <DesignSuggestionImage src={src} alt={alt} className="size-full object-cover" />
          </button>
        ) : (
          <DesignSuggestionImage src={src} alt={alt} className="size-full object-cover" />
        )
      ) : null}
      {onRemove && !loading && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? `Remove ${alt}`}
          className="absolute right-0.5 top-0.5 rounded-full bg-background/85 p-0.5 text-foreground/70 shadow-sm transition-colors hover:text-foreground"
        >
          <X size={10} weight="bold" />
        </button>
      )}
    </div>
  );
}

/** A file upload still in flight — its own local id, never a `DesignUploadDraft` yet.
 *  Exported (FRONT-2382 review #3) — `NewProjectView` now owns this list so the
 *  Create button and its Cmd/Ctrl+Enter shortcut can gate on it too. */
export interface UploadInFlight {
  localId: string;
  name: string;
}

/** "Inspiration & references" — the section's one free-text field. */
function InspirationField({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[length:var(--text-sm)] font-medium text-foreground"
        style={{ marginBottom: 6 }}
      >
        Inspiration & references
      </label>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={MAX_DESIGN_INSPIRATION_CHARS}
        rows={3}
        placeholder="Sites, products or styles you like…"
        disabled={disabled}
        className="resize-none rounded-[10px] shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-[3px] focus-visible:ring-ring/40"
      />
    </div>
  );
}

/** "Reference images" — staged upload thumbnails plus the drop/pick area. */
function ReferenceImagesField({
  uploads,
  uploading,
  gate,
  disabled,
  onFiles,
  onRemove,
}: {
  uploads: { fileId: string; name: string }[];
  uploading: UploadInFlight[];
  gate: DesignGateState;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  onRemove: (fileId: string) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const blocked = gate.disabled || disabled;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3" style={{ marginBottom: 6 }}>
        <span className="text-[length:var(--text-sm)] font-medium text-foreground">Reference images</span>
        {gate.disabled && <span className="text-[length:var(--text-xs)] text-muted-foreground">{gate.reason}</span>}
      </div>
      {(uploads.length > 0 || uploading.length > 0) && (
        <div className="mb-2 flex flex-wrap gap-2">
          {uploads.map((u) => (
            <DesignReferenceThumb
              key={u.fileId}
              src={rawFileUrl(u.fileId)}
              alt={u.name}
              testId="design-reference-thumb"
              onRemove={disabled ? undefined : () => onRemove(u.fileId)}
              removeLabel={`Remove ${u.name}`}
            />
          ))}
          {uploading.map((u) => (
            <DesignReferenceThumb key={u.localId} loading alt={u.name} />
          ))}
        </div>
      )}
      <label
        onDragOver={(e) => {
          e.preventDefault();
          if (!blocked) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (blocked) return;
          onFiles(Array.from(e.dataTransfer.files));
        }}
        className={cn(
          'flex w-full cursor-pointer items-center justify-center rounded-[10px] border border-dashed border-border px-3 py-3 text-center text-[length:var(--text-sm)] text-muted-foreground transition-colors',
          isDragging && 'border-primary/60 bg-primary/5',
          blocked && 'cursor-not-allowed opacity-50',
        )}
      >
        + Drop mockups or screenshots
        <input
          type="file"
          multiple
          accept={DESIGN_REFERENCE_IMAGE_ACCEPT}
          className="hidden"
          disabled={blocked}
          onChange={(e) => {
            onFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </label>
    </div>
  );
}

/**
 * One "Suggest directions from the web" result card (feedback round 2:
 * clicking the card body now opens the large zoomed viewer; picking is a
 * small round control in the corner, `design-suggestion-pick`, so the two
 * gestures don't collide). The card itself can't be a native `<button>`
 * anymore — it now contains one (the pick control) and a button can't
 * nest a button — so it's a `role="button"` div with its own Enter/Space
 * handling, matching the accessible-name and keyboard contract a real
 * button gets for free.
 */
function SuggestionCard({
  suggestion,
  picked,
  pickDisabled,
  viewDisabled,
  onTogglePick,
  onOpen,
}: {
  suggestion: DesignSuggestion;
  picked: boolean;
  pickDisabled?: boolean;
  viewDisabled?: boolean;
  onTogglePick: () => void;
  onOpen: () => void;
}) {
  const canOpen = !viewDisabled;
  return (
    <div
      data-testid="design-suggestion"
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      aria-label={canOpen ? `View ${suggestion.title} large` : undefined}
      onClick={canOpen ? onOpen : undefined}
      onKeyDown={
        canOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      className={cn(
        'relative flex flex-col overflow-hidden rounded-[10px] border text-left transition-colors',
        canOpen && 'cursor-pointer',
        picked ? 'border-primary ring-1 ring-primary/40' : 'border-border hover:border-foreground/30',
      )}
    >
      <span className="relative block aspect-[4/3] w-full overflow-hidden bg-muted">
        <DesignSuggestionImage
          src={suggestion.imageUrl}
          alt={suggestion.title}
          className="size-full object-cover"
          testId="design-suggestion-thumb"
        />
        <button
          type="button"
          data-testid="design-suggestion-pick"
          aria-pressed={picked}
          aria-label={picked ? `Unpick ${suggestion.title}` : `Pick ${suggestion.title}`}
          disabled={pickDisabled}
          onClick={(e) => {
            // The card body underneath opens the viewer — picking must not
            // also trigger that.
            e.stopPropagation();
            onTogglePick();
          }}
          className={cn(
            'absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            picked
              ? 'bg-primary text-primary-foreground'
              : 'bg-background/85 text-foreground/70 hover:text-foreground',
          )}
        >
          {picked && <Check size={12} weight="bold" />}
        </button>
      </span>
      <span className="p-2">
        <span className="block text-[length:var(--text-sm)] font-medium text-foreground">{suggestion.title}</span>
        <span className="block text-[length:var(--text-xs)] text-muted-foreground [text-wrap:pretty]">
          {suggestion.description}
        </span>
      </span>
    </div>
  );
}

/** "Let EDDY suggest" — the suggest/more button, its error, and the card grid.
 *  Exported (FRONT-2382 review #2) so its own test can render the REAL
 *  production component through `renderToStaticMarkup` with controlled
 *  props, rather than reimplementing its markup or driving the whole
 *  `DesignDirectionDisclosure` through its internal, un-drivable
 *  `hasSuggested`/`suggestions` state (this repo's frontend suite has no
 *  DOM, so there is no way to click a button and observe a state update). */
export function SuggestionsField({
  hasSuggested,
  pickedCount,
  suggestGate,
  suggesting,
  suggestError,
  suggestions,
  isPicked,
  onSuggest,
  onTogglePick,
  disabled,
}: {
  hasSuggested: boolean;
  pickedCount: number;
  suggestGate: DesignGateState;
  suggesting: boolean;
  suggestError: string | null;
  suggestions: DesignSuggestion[];
  isPicked: (id: string) => boolean;
  onSuggest: () => void;
  onTogglePick: (suggestion: DesignSuggestion) => void;
  disabled?: boolean;
}) {
  // FRONT-2382 review #2: the cards themselves already go disabled at the
  // cap (each card asks its own `pickGateState`, below) — but nothing said
  // WHY next to the counter, so hitting the cap read as the grid going
  // randomly unresponsive. `picked: false` is deliberate: this is never
  // about one specific card, only "is a NEW pick possible right now".
  const capGate = pickGateState({ picked: false, pickedCount });
  // Feedback round 2: which suggestion (if any) is open large right now —
  // an index into `suggestions`, so "on-screen order" for the arrows is
  // simply that same array's order.
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3" style={{ marginBottom: 6 }}>
        <span className="text-[length:var(--text-sm)] font-medium text-foreground">Let EDDY suggest</span>
        {hasSuggested && (
          <span className="text-[length:var(--text-xs)] text-muted-foreground tabular-nums">
            {pickedCount} of {MAX_DESIGN_PICKS} picked
          </span>
        )}
      </div>
      {hasSuggested && capGate.disabled && (
        <p
          className="text-[length:var(--text-xs)] text-muted-foreground"
          style={{ margin: '0 0 6px' }}
        >
          {capGate.reason}
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={suggestGate.disabled || disabled}
        tooltip={suggestGate.reason}
        onClick={onSuggest}
        className="gap-1.5"
      >
        {suggesting && <CircleNotch size={13} className="animate-spin" />}
        {hasSuggested ? 'More suggestions' : 'Suggest directions from the web'}
      </Button>
      {/* Visible text, not just a hover hint — a reason a person must act on
          belongs on screen, not only under the pointer (CLAUDE.md's
          disabled-control rule). */}
      {suggestGate.disabled && (
        <p className="text-[length:var(--text-xs)] text-muted-foreground" style={{ margin: '6px 0 0' }}>
          {suggestGate.reason}
        </p>
      )}
      {suggestError && (
        <p className="text-[length:var(--text-xs)] text-destructive [text-wrap:pretty]" style={{ margin: '6px 0 0' }}>
          {suggestError}
        </p>
      )}
      {suggestions.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {suggestions.map((s, i) => {
            const picked = isPicked(s.id);
            const gate = pickGateState({ picked, pickedCount });
            return (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                picked={picked}
                pickDisabled={gate.disabled || disabled}
                viewDisabled={disabled}
                onTogglePick={() => onTogglePick(s)}
                onOpen={() => setViewerIndex(i)}
              />
            );
          })}
        </div>
      )}
      {viewerIndex !== null && (
        <DesignSuggestionViewer
          items={suggestions}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
          pick={{
            isPicked,
            pickedCount,
            // The viewer's own item type carries an OPTIONAL description
            // (it also renders the Project-context dialog's reference
            // viewer, where that's genuinely absent) — `DesignSuggestion`'s
            // is required, so resolve back to the real suggestion by id
            // rather than widening/narrowing either type to paper over it.
            onTogglePick: (item) => {
              const suggestion = suggestions.find((s) => s.id === item.id);
              if (suggestion) onTogglePick(suggestion);
            },
          }}
        />
      )}
    </div>
  );
}

/**
 * The New-project form's folded "Design direction · Optional" section
 * (FRONT-2382). A controlled component in the `useState` dispatch sense —
 * `value` is the parent's committed draft, `onChange` its raw setter — so an
 * async upload's `onChange((prev) => …)` always folds onto the LATEST draft
 * rather than a snapshot captured when the upload started (which would lose
 * an earlier file's entry when two uploads finish out of order).
 *
 * `name`/`context` come from the form's own two top fields — the "Suggest
 * directions from the web" request needs at least one of them (see
 * `suggestGateState`), and they ride the request body so the model has
 * something to search for.
 */
export function DesignDirectionDisclosure({
  name,
  context,
  value,
  onChange,
  uploading,
  onUploadingChange,
  disabled,
}: {
  name: string;
  context: string;
  value: DesignDirectionDraft;
  onChange: Dispatch<SetStateAction<DesignDirectionDraft>>;
  /** FRONT-2382 review #3: lifted to `NewProjectView` — the 5-cap already
   *  needed to count a queued-but-not-yet-landed file, and the Create button
   *  needs the SAME count to know whether a submit would race an upload, so
   *  one piece of state serves both rather than two copies drifting. */
  uploading: UploadInFlight[];
  onUploadingChange: Dispatch<SetStateAction<UploadInFlight[]>>;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const inspirationId = useId();
  // FRONT-2382 review: the upload flow (queueing, the 5-cap, the parallel
  // requests) and the suggestion flow (the request, its fetched results,
  // toggling a pick) each own their own state and wiring in a dedicated
  // hook — this component only renders the fields below.
  const uploads = useDesignReferenceUploads({ value, onChange, uploading, onUploadingChange });
  const picks = useDesignSuggestionPicks({ name, context, value, onChange });

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        disabled={disabled}
        className="flex w-full items-center justify-between gap-3 rounded-[10px] border border-border bg-transparent px-3.5 py-2.5 text-left transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="flex items-baseline gap-2">
          <span className="text-[length:var(--text-sm)] font-medium text-foreground">Design direction</span>
          <span className="text-[length:var(--text-xs)] text-muted-foreground">Optional</span>
        </span>
        <CaretDown
          size={14}
          className={cn('shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      {/* CSS-hidden rather than unmounted while collapsed, so fetched
          suggestions and in-progress uploads survive an accidental
          collapse/reopen instead of quietly vanishing. */}
      <div className={cn('space-y-5', open ? 'mt-3 block' : 'hidden')}>
        <InspirationField
          id={inspirationId}
          value={value.inspiration}
          onChange={(next) => onChange((prev) => ({ ...prev, inspiration: next }))}
          disabled={disabled}
        />

        <ReferenceImagesField
          uploads={value.uploads}
          uploading={uploading}
          gate={uploads.gate}
          disabled={disabled}
          onFiles={uploads.onFiles}
          onRemove={uploads.onRemove}
        />

        <div>
          <p className="text-[length:var(--text-sm)] font-medium text-foreground" style={{ marginBottom: 6 }}>
            How should EDDY use them?
          </p>
          <DesignDirectiveChips
            selected={value.directives}
            onToggle={(directive) =>
              onChange((prev) => ({
                ...prev,
                directives: prev.directives.includes(directive)
                  ? prev.directives.filter((d) => d !== directive)
                  : [...prev.directives, directive],
              }))
            }
            disabled={disabled}
          />
        </div>

        <SuggestionsField
          hasSuggested={picks.hasSuggested}
          pickedCount={value.picks.length}
          suggestGate={picks.suggestGate}
          suggesting={picks.suggesting}
          suggestError={picks.suggestError}
          suggestions={picks.suggestions}
          isPicked={picks.isPicked}
          onSuggest={picks.onSuggest}
          onTogglePick={picks.onTogglePick}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
